import React, { useContext, useState } from "react";
import {
  TrendingUp,
  Users,
  Clock,
  MessageSquare,
  Send,
  Download,
  CheckCircle,
  Hourglass,
  BarChart2,
  Star,
  Award,
  Activity,
  AlertTriangle,
  RefreshCw,
  XCircle,
  ArrowUpRight,
  HardDrive
} from "lucide-react";

import { Redirect } from "react-router-dom";

import UserSelect from "../../components/UserSelect";
import { checkPermission } from "../../rules";
import useTickets from "../../hooks/useTickets";
import useDashboard from "../../hooks/useDashboard";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

import Chart from "./Chart";
import TicketsEvolutionChart from "./TicketsEvolutionChart";
import TicketsQueueChart from "./TicketsQueueChart";
import TicketsTrackingChart from "./TicketsTrackingChart";
import MessagesTrafficChart from "./MessagesTrafficChart";

const renderStars = (rating) => {
  const stars = [];
  const roundedRating = Math.round(rating * 2) / 2;
  for (let i = 1; i <= 5; i++) {
    if (i <= roundedRating) {
      stars.push(<Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />);
    } else if (i - 0.5 === roundedRating) {
      stars.push(
        <div key={i} className="relative inline-block w-4 h-4">
          <Star className="w-4 h-4 text-slate-200 dark:text-slate-700" />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
          </div>
        </div>
      );
    } else {
      stars.push(<Star key={i} className="w-4 h-4 text-slate-200 dark:text-slate-700" />);
    }
  }
  return <div className="flex gap-0.5 justify-center sm:justify-start">{stars}</div>;
};

const PremiumMetricCard = ({ title, value, icon: Icon, gradient, subtitle, statusText, statusType }) => {
  const statusColors = {
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20",
    danger: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20",
    info: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20",
  };

  return (
    <div className="relative group overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-slate-200/80 dark:hover:border-slate-700/80 backdrop-blur-md">
      {/* Background soft gradient blob */}
      <div className={`absolute -right-12 -bottom-12 w-32 h-32 rounded-full bg-gradient-to-tr ${gradient} opacity-10 group-hover:scale-125 transition-transform duration-500 blur-xl`} />
      
      <div className="flex items-start justify-between">
        <div className={`rounded-xl p-3 bg-gradient-to-tr ${gradient} text-white shadow-sm shadow-indigo-500/10`}>
          <Icon className="h-5 w-5" />
        </div>
        {statusText && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider uppercase ${statusColors[statusType] || statusColors.info}`}>
            {statusText}
          </span>
        )}
      </div>

      <div className="mt-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{title}</p>
        <div className="mt-1 flex items-baseline gap-2">
          <p className="text-3xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">
            {value}
          </p>
        </div>
        {subtitle && (
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 font-medium">
             {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusTile = ({ title, value, icon: Icon, colorClass, borderClass, bgClass, subtitle }) => (
  <div className={`rounded-2xl border-l-4 ${borderClass} border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-5 shadow-sm transition-all hover:shadow-md backdrop-blur-sm`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{title}</p>
        <p className={`mt-1.5 text-3xl font-extrabold tracking-tight ${colorClass}`}>{value}</p>
        <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500 font-medium">{subtitle}</p>
      </div>
      <div className={`rounded-xl p-3 ${bgClass}`}>
        <Icon className={`h-6 w-6 ${colorClass}`} />
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, height = "h-72", children, actions }) => (
  <div className="rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-6 shadow-sm flex flex-col backdrop-blur-md">
    <div className="flex items-center justify-between mb-5">
      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 tracking-tight flex items-center gap-2">
        <Activity className="w-4 h-4 text-indigo-500" />
        {title}
      </h3>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
    <div className={`flex-1 ${height} min-h-0 w-full`}>{children}</div>
  </div>
);

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [filterParams, setFilterParams] = useState({});
  const { counters, loading } = useDashboard(filterParams);

  const userQueueIds =
    user.queues && user.queues.length > 0 ? user.queues.map((q) => q.id) : [];

  const openTicketsHook = useTickets({
    status: "open",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  const pendingTicketsHook = useTickets({
    status: "pending",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  const closedTicketsHook = useTickets({
    status: "closed",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  const {
    ticketsByUser,
    ticketsByQueue,
    messagesByStatus,
    messagesTraffic,
    avgResolutionTime,
    ticketsByDay,
    avgFirstResponseTime = 0,
    abandonmentRate = 0,
    reopenedRate = 0,
    topAgents = [],
    counts,
    storageLimit = 5368709120,
    storageUsed = 0
  } = counters || {};

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const storagePercent = Math.min(100, Math.round((storageUsed / storageLimit) * 100)) || 0;

  const totalMessages =
    messagesByStatus?.reduce((acc, curr) => acc + parseInt(curr.count), 0) || 0;
  const sentMessages = messagesByStatus?.find((m) => m.fromMe)?.count || 0;
  const receivedMessages = messagesByStatus?.find((m) => !m.fromMe)?.count || 0;

  const currentOpen = counts?.active ?? openTicketsHook.count;
  const currentPending = counts?.pending ?? pendingTicketsHook.count;
  const currentClosed = counts?.closed ?? closedTicketsHook.count;

  // Determine Response Time Rating Badge
  let responseRating = { text: "Excelente", type: "success" };
  if (avgFirstResponseTime >= 15) {
    responseRating = { text: "Crítico", type: "danger" };
  } else if (avgFirstResponseTime >= 5) {
    responseRating = { text: "Regular", type: "warning" };
  }

  // Determine CSAT based on overall response time average
  const calculateCSAT = () => {
    if (avgFirstResponseTime === 0) return 4.8;
    if (avgFirstResponseTime < 5) return 4.7;
    if (avgFirstResponseTime < 15) return 4.4;
    if (avgFirstResponseTime < 30) return 3.9;
    return 3.1;
  };
  const generalCSAT = counters?.overallCSAT > 0 ? counters.overallCSAT : calculateCSAT();

  // "/" is the landing route, so a role scoped to a single section (e.g.
  // Profesional, who only handles appointments) would otherwise land on a
  // dashboard it cannot read. Checked after every hook so the hook order
  // stays stable across renders.
  if (
    user.profile !== "admin" &&
    Array.isArray(user.permissions) &&
    user.permissions.length > 0 &&
    !checkPermission(user.permissions, "dashboard:view")
  ) {
    return (
      <Redirect
        to={
          checkPermission(user.permissions, "appointments:view")
            ? "/appointments"
            : "/tickets"
        }
      />
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5">
        <div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-500 bg-clip-text text-transparent tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
            Supervisa e impulsa la eficiencia conversacional en tiempo real
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200/50 dark:border-slate-800 px-3 py-1.5 rounded-xl shadow-inner w-full sm:w-auto">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Filtrar:</span>
            <div className="w-full sm:w-48">
              <UserSelect
                selectedUsers={filterParams.userId || ""}
                onChange={(e) => setFilterParams((prev) => ({ ...prev, userId: e }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Status Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatusTile
          title={i18n.t("dashboard.messages.inAttendance.title")}
          value={currentOpen}
          icon={MessageSquare}
          colorClass="text-indigo-600 dark:text-indigo-400"
          borderClass="border-l-indigo-500"
          bgClass="bg-indigo-50 dark:bg-indigo-950/20"
          subtitle="Chats que están siendo atendidos ahora"
        />
        <StatusTile
          title={i18n.t("dashboard.messages.waiting.title")}
          value={currentPending}
          icon={Hourglass}
          colorClass="text-amber-500 dark:text-amber-400"
          borderClass="border-l-amber-500"
          bgClass="bg-amber-50 dark:bg-amber-950/20"
          subtitle="Mensajes en espera por un agente"
        />
        <StatusTile
          title={i18n.t("dashboard.messages.closed.title")}
          value={currentClosed}
          icon={CheckCircle}
          colorClass="text-emerald-500 dark:text-emerald-400"
          borderClass="border-l-emerald-500"
          bgClass="bg-emerald-50 dark:bg-emerald-950/20"
          subtitle="Conversaciones resueltas con éxito"
        />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <div className="relative flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin" />
            <RefreshCw className="w-5 h-5 text-indigo-500 absolute animate-pulse" />
          </div>
          <p className="text-sm font-semibold mt-4 text-slate-400 dark:text-slate-500">Analizando base de datos...</p>
        </div>
      ) : (
        <>
          {/* Advanced Premium KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <PremiumMetricCard
              title="T. Respuesta Inicial (FRT)"
              value={avgFirstResponseTime > 0 ? `${avgFirstResponseTime} min` : "0 min"}
              icon={Clock}
              gradient="from-violet-500 to-indigo-600"
              subtitle="Promedio desde entrada a atención"
              statusText={responseRating.text}
              statusType={responseRating.type}
            />
            <PremiumMetricCard
              title="T. Promedio de Resolución"
              value={avgResolutionTime?.avgMinutes ? `${parseInt(avgResolutionTime.avgMinutes)} min` : "0 min"}
              icon={CheckCircle}
              gradient="from-pink-500 to-purple-600"
              subtitle="Conversaciones resueltas"
              statusText="ART"
              statusType="info"
            />
            <PremiumMetricCard
              title="Satisfacción de Cliente (CSAT)"
              value={`${generalCSAT.toFixed(1)} / 5.0`}
              icon={Star}
              gradient="from-amber-400 to-orange-500"
              subtitle={renderStars(generalCSAT)}
              statusText="Estimado"
              statusType="warning"
            />
            <PremiumMetricCard
              title="Almacenamiento del Servidor"
              value={`${formatBytes(storageUsed)} / ${formatBytes(storageLimit, 0)}`}
              icon={HardDrive}
              gradient="from-indigo-500 to-cyan-500"
              subtitle={
                <div className="w-full mt-2">
                  <div className="flex justify-between items-center text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1 font-semibold">
                    <span>Espacio Usado</span>
                    <span>{storagePercent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        storagePercent > 90
                          ? "bg-rose-500"
                          : storagePercent > 70
                          ? "bg-amber-500"
                          : "bg-indigo-600"
                      }`}
                      style={{ width: `${storagePercent}%` }}
                    />
                  </div>
                </div>
              }
              statusText={storagePercent > 90 ? "Lleno" : storagePercent > 70 ? "Advertencia" : "Saludable"}
              statusType={storagePercent > 90 ? "danger" : storagePercent > 70 ? "warning" : "success"}
            />
            <PremiumMetricCard
              title="Tráfico Total Mensajes"
              value={totalMessages.toLocaleString()}
              icon={TrendingUp}
              gradient="from-blue-500 to-cyan-600"
              subtitle={`Enviados: ${Number(sentMessages).toLocaleString()} | Recibidos: ${Number(receivedMessages).toLocaleString()}`}
              statusText="Volumen"
              statusType="info"
            />
            <PremiumMetricCard
              title="Tasa de Abandono"
              value={`${abandonmentRate}%`}
              icon={XCircle}
              gradient="from-red-400 to-rose-600"
              subtitle="Cerrados directamente sin respuesta"
              statusText={abandonmentRate > 15 ? "Alto" : "Bajo"}
              statusType={abandonmentRate > 15 ? "danger" : "success"}
            />
            <PremiumMetricCard
              title="Tasa de Reabiertos"
              value={`${reopenedRate}%`}
              icon={Activity}
              gradient="from-teal-400 to-emerald-600"
              subtitle="Conversaciones vueltas a abrir"
              statusText={reopenedRate > 10 ? "Atención" : "Estable"}
              statusType={reopenedRate > 10 ? "warning" : "success"}
            />
          </div>

          {/* Charts Rows */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <ChartCard title="Evolución de Conversaciones">
                <TicketsEvolutionChart data={ticketsByDay} />
              </ChartCard>
            </div>
            <div>
              <ChartCard title="Distribución por Departamento">
                <TicketsQueueChart data={ticketsByQueue} />
              </ChartCard>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <div className="rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-6 shadow-sm backdrop-blur-md h-full">
                <MessagesTrafficChart data={messagesTraffic} />
              </div>
            </div>
            <div className="h-full flex flex-col justify-between rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-6 shadow-sm backdrop-blur-md">
              <div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 tracking-tight flex items-center gap-2 mb-4">
                  <BarChart2 className="w-4 h-4 text-indigo-500" />
                  Rendimiento de Carga
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Gráfico detallado de transición e inactividad de los tickets a través de los diferentes estados en el embudo de soporte técnico y comercial de la empresa.
                </p>
              </div>
              <div className="mt-4 bg-slate-50 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-center text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  <span>Métrica</span>
                  <span>Meta SLA</span>
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-300 font-medium">
                    <span>Respuesta (FRT)</span>
                    <span className="text-emerald-500 font-semibold">&lt; 5 min</span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: "80%" }} />
                  </div>
                  <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-300 font-medium">
                    <span>Resolución (ART)</span>
                    <span className="text-indigo-500 font-semibold">&lt; 30 min</span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-indigo-500 h-full rounded-full" style={{ width: "65%" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Tracking Chart Details */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 shadow-sm backdrop-blur-md">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 tracking-tight flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-indigo-500" />
              Detalle de Tiempos Operativos de Ticket
            </h3>
            <TicketsTrackingChart />
          </div>

          {/* Agent Leaderboard & Performance Dashboard */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 shadow-sm backdrop-blur-md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-bold text-slate-850 dark:text-slate-100 tracking-tight">
                  Desempeño de Agentes (Leaderboard)
                </h2>
              </div>
              <p className="text-xs text-slate-400 font-medium">
                Clasificación de agentes por productividad y satisfacción
              </p>
            </div>

            {topAgents.length === 0 ? (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">
                No hay actividad registrada de agentes en este periodo.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800">
                <table className="min-w-[650px] w-full divide-y divide-slate-100 dark:divide-slate-800 text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4 whitespace-nowrap">Agente</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Chats Activos</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Chats Resueltos</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">T. Respuesta (FRT)</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">T. Resolución (ART)</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Satisfacción (CSAT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 bg-white dark:bg-slate-900/10 text-slate-700 dark:text-slate-300">
                    {topAgents
                      .sort((a, b) => b.closed - a.closed || a.avgResponseTime - b.avgResponseTime)
                      .map((agent, index) => {
                        const gradientList = [
                          "from-indigo-500 to-purple-500",
                          "from-emerald-400 to-teal-600",
                          "from-pink-500 to-rose-500",
                          "from-amber-400 to-orange-500",
                          "from-blue-500 to-cyan-600"
                        ];
                        const grad = gradientList[index % gradientList.length];

                        return (
                          <tr key={agent.userId} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                            <td className="px-6 py-4 flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full bg-gradient-to-tr ${grad} text-white font-bold flex items-center justify-center shadow-sm`}>
                                {agent.name[0].toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{agent.name}</span>
                                {index === 0 && (
                                  <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 font-extrabold text-[8px] uppercase tracking-wider">
                                    Top Agente
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center font-bold text-indigo-600 dark:text-indigo-400">{agent.active}</td>
                            <td className="px-6 py-4 text-center font-bold text-emerald-600 dark:text-emerald-400">{agent.closed}</td>
                            <td className="px-6 py-4 text-center font-medium">
                              <span className={agent.avgResponseTime > 15 ? "text-rose-500 font-semibold" : agent.avgResponseTime > 5 ? "text-amber-500" : "text-slate-600 dark:text-slate-400"}>
                                {agent.avgResponseTime} min
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400 font-medium">{agent.avgResolutionTime} min</td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex flex-col items-center sm:items-start gap-1 justify-center">
                                <span className="font-extrabold text-amber-500 flex items-center gap-1">{agent.csat} ★</span>
                                {renderStars(agent.csat)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Daily Hourly Ticket creation breakdown */}
          <ChartCard title="Carga Horaria de Entrada">
            <Chart />
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default Dashboard;

